import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { Toaster } from "sonner";
import { App as CapacitorApp } from '@capacitor/app';
import SimpleSplash from './components/SimpleSplash';

// Pages
import { Home } from "./pages/Home";
import { DailyLog } from "./pages/DailyLog";
import { History } from "./pages/History";
import { HistoricalView } from "./pages/HistoricalView";
import { Export } from "./pages/Export";
import { Settings } from "./pages/Settings";
import { Help } from "./pages/Help";
import { Onboarding } from "./pages/Onboarding";
import { Passcode } from "./pages/Passcode";
import { AIResponse } from "./pages/AIResponse";

// Components
import { Navigation } from "./components/Navigation";
import { ThemeProvider } from "./components/ThemeProvider";

// Utils
import { hasCompletedOnboarding, getSecurityType, SECURITY_TYPE, getColorTheme, getTextScale, applyTextScale } from "./utils/storage";
import { scheduleNotification, initializeNotifications } from "./utils/notifications";
import { isBiometricsAvailable } from './utils/biometricAuth';
import { applyThemeIcon } from './utils/themeIcon';

import "./App.css";

function App() {
  const toastTopOffset = 'calc(env(safe-area-inset-top, 0px) + 92px)';
  const toastLeftOffset = 'calc(env(safe-area-inset-left, 0px) + 12px)';
  const toastMaxWidth = 'min(520px, calc(100vw - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px) - 32px))';

  const [showSplash, setShowSplash] = useState(true);
  const [isOnboarded, setIsOnboarded] = useState(hasCompletedOnboarding());
  const [isLocked, setIsLocked] = useState(false);
  const [checkComplete, setCheckComplete] = useState(false);
  const [biometricsAvailable, setBiometricsAvailable] = useState(false);

  const handleSplashComplete = () => {
    setShowSplash(false);
  };

  // First useEffect: Check onboarding and security
  useEffect(() => {
    console.log('🔒 App: Initializing...');
    
    const initialize = async () => {
      // Keep app icon aligned with saved theme even when app starts on the lock screen.
      void applyThemeIcon(getColorTheme());
      applyTextScale(getTextScale());

      const onboarded = hasCompletedOnboarding();
      setIsOnboarded(onboarded);
      
      // Check security type properly
      const securityType = getSecurityType();
      const hasSecurity = securityType === SECURITY_TYPE.PASSCODE || securityType === SECURITY_TYPE.BIOMETRIC;
      
      console.log('🔒 App: Security type:', securityType);
      console.log('🔒 App: Has security:', hasSecurity);
      
      if (onboarded && hasSecurity) {
        console.log('🔒 App: Security enabled, app locked');
        setIsLocked(true);
      } else {
        console.log('🔒 App: No security, app unlocked');
        setIsLocked(false);
      }
      
      setCheckComplete(true);

      // Setup notifications
      const setupNotifications = async () => {
        await initializeNotifications();
        const notifSettings = await import('./utils/storage').then(m => m.getNotificationSettings());
        if (notifSettings.enabled) {
          await scheduleNotification(notifSettings.time);
        }
      };
      setupNotifications();
    };
    
    initialize();
  }, []);

  // Second useEffect: Check biometrics availability
  useEffect(() => {
    const checkBiometrics = async () => {
      const available = await isBiometricsAvailable();
      console.log('📱 App: Biometrics available:', available);
      setBiometricsAvailable(available);
    };
    checkBiometrics();
  }, []);

  // Third useEffect: Listen for app state changes
  useEffect(() => {
    const handleAppStateChange = ({ isActive }) => {
      console.log('🔒 App: App state changed, isActive:', isActive);
      if (!isActive && isOnboarded) {
        const securityType = getSecurityType();
        const hasSecurity = securityType === SECURITY_TYPE.PASSCODE || securityType === SECURITY_TYPE.BIOMETRIC;
        if (hasSecurity) {
          console.log('🔒 App: App went to background, locking');
          setIsLocked(true);
        }
      }
    };
    
    CapacitorApp.addListener('appStateChange', handleAppStateChange);
    
    return () => {
      CapacitorApp.removeAllListeners();
    };
  }, [isOnboarded]);

  const handleUnlock = () => {
    console.log('🔓 App: handleUnlock called, setting isLocked to false');
    setIsLocked(false);
  };

  const handleOnboardingComplete = () => {
    console.log('🔓 App: Onboarding complete');
    setIsOnboarded(true);
  };

  console.log('🔒 App: Rendering, isLocked:', isLocked, 'isOnboarded:', isOnboarded, 'checkComplete:', checkComplete);

  // Show splash screen first
  if (showSplash) {
    return <SimpleSplash onComplete={handleSplashComplete} />;
  }

  // Show loading spinner while checking security
  if (!checkComplete) {
    return (
      <div className="fixed inset-0 bg-app flex items-center justify-center">
        <div
          className="w-8 h-8 rounded-full border-2 animate-spin"
          style={{ borderColor: 'var(--theme-primary)', borderTopColor: 'var(--theme-accent)' }}
        />
      </div>
    );
  }

  // Show passcode screen if locked
  if (isLocked && isOnboarded) {
    console.log('🔒 App: Rendering Passcode screen');
    return (
      <>
        <Toaster 
          position="top-left" 
          offset={{ top: toastTopOffset, left: toastLeftOffset }}
          mobileOffset={{ top: toastTopOffset, left: toastLeftOffset }}
          toastOptions={{
            className: 'font-body text-left',
            style: {
              width: toastMaxWidth,
              maxWidth: toastMaxWidth,
              background: 'var(--theme-light, #F4F1FA)',
              border: '1px solid var(--theme-secondary, #E9E3F5)',
              color: 'var(--theme-text-primary, #2D2D35)',
            },
          }}
        />
        <Passcode onUnlock={handleUnlock} />
      </>
    );
  }

  // Show main app
  return (
    <ThemeProvider>
      <div className="App font-body">
        <Toaster 
          position="top-left" 
          offset={{ top: toastTopOffset, left: toastLeftOffset }}
          mobileOffset={{ top: toastTopOffset, left: toastLeftOffset }}
          toastOptions={{
            className: 'font-body text-left',
            style: {
              width: toastMaxWidth,
              maxWidth: toastMaxWidth,
              background: 'var(--theme-light, #F4F1FA)',
              border: '1px solid var(--theme-secondary, #E9E3F5)',
              color: 'var(--theme-text-primary, #2D2D35)',
            },
          }}
        />
        <BrowserRouter>
          <AnimatePresence mode="wait">
            <Routes>
              {!isOnboarded ? (
                <Route path="*" element={<Onboarding onComplete={handleOnboardingComplete} />} />
              ) : (
                <>
                  <Route path="/" element={<Home />} />
                  <Route path="/daily-log" element={<DailyLog />} />
                  <Route path="/history" element={<History />} />
                  <Route path="/historical-view/:date" element={<HistoricalView />} />
                  <Route path="/export" element={<Export />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/ai-response" element={<AIResponse />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </>
              )}
            </Routes>
          </AnimatePresence>
          {isOnboarded && <Navigation />}
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;